import type { ConnectionPhase } from '@parsleyai/ui'
import { collection, type DocumentData, onSnapshot, type QuerySnapshot } from 'firebase/firestore'
import { createContext, type ReactNode, useEffect, useReducer } from 'react'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'
import type { MealPlanResult } from '@/types/mealPlan'
import { initialMealPlanResultsState, mealPlanResultsReducer } from './mealPlanResultsReducer'
import { MealPlanResultsActionTypes, type MealPlanResultsState } from './types'

export interface MealPlanResultsContextValue {
  state: MealPlanResultsState
  /** Live-connection phase for the meal-plan subscription — drives the reconnecting banner. */
  phase: ConnectionPhase
  /** Manual resubscribe (the banner's "Try now"). */
  retry: () => void
}

export const MealPlanResultsContext = createContext<MealPlanResultsContextValue | undefined>(undefined)

interface MealPlanResultsProviderProps {
  children: ReactNode
}

// Read-only slice: meal-plan result docs are written by the backend listener
// (onMealPlanRequestCreated) after the engine generates a meal. The client only
// subscribes — there are no mutation methods here. The subscription auto-resubscribes
// on error (useLiveSubscription): the last-good data stays rendered while it reconnects,
// and `phase` surfaces the reconnecting/restored/stalled banner state.
export function MealPlanResultsProvider({ children }: MealPlanResultsProviderProps) {
  const { uid } = useUserData()
  const [state, dispatch] = useReducer(mealPlanResultsReducer, initialMealPlanResultsState)

  // Drop cached plans on sign-out; the subscription itself is disabled by the null key below.
  useEffect(() => {
    if (!uid) dispatch({ type: MealPlanResultsActionTypes.CLEAR })
  }, [uid])

  const { phase, retry } = useLiveSubscription<QuerySnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'mealPlanResults',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(collection(db, 'users', activeUid, 'meal_plan_results'), onData, onError),
    onData: (snapshot) => {
      const mealPlanResults = snapshot.docs
        // Soft-deleted plans (deletedAt set) are retained in Firestore for
        // auditing/liability but hidden from every user-facing surface.
        .filter((d) => !d.data().deletedAt)
        .map((d) => {
          const data = d.data()
          // Older result docs predate this field; default to false when absent.
          const levelUpsEnabled = data.levelUpsEnabled ?? false
          return { id: d.id, ...data, levelUpsEnabled } as MealPlanResult
        })
        .sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0))
      dispatch({ type: MealPlanResultsActionTypes.SET, payload: mealPlanResults })
    }
  })

  return <MealPlanResultsContext.Provider value={{ state, phase, retry }}>{children}</MealPlanResultsContext.Provider>
}
